import { useCallback, useMemo, useState } from "react";
import { AircraftWbStation } from "../../models/aircraft";
import Flight, { FlightFuelOnBoard, FlightWb, FlightWbLoad } from "../../models/flight";
import { FlightLegInfo, getFlightLegs } from "../../models/flightLegs";
import {
  CalculatedFlightWbPoints,
  calculateOutOfLimitsTexts,
  calculateStationLimitTexts,
  calculateWarningOutOfLimitsTexts,
  generateLegWbLoads,
  generateSeatWbLoads,
  getCalculatedFlightWbPoints,
} from "../../models/flightWb";
import { FuelConverter } from "../../utils/FuelUtils";
import { WbLoadsheetInput } from "../../utils/wbLoadsheet";

/**
 * State and editing for a W&B sheet — one per leg for flights with intermediate stops. Shared by the
 * planning page (FlightWbPage) and the pilot's last-minute revision (FlyWbRevisionPage) so both use
 * the exact same seating, fuel, math and PDF input.
 */

type LegState = {
  loads: FlightWbLoad[];
  //undefined = carried over: the leg departs with the previous leg's landing fuel. Set = uplift
  //(or, on leg 1 of a revision, the pilot's actual fuel).
  fuelOnBoard?: FlightFuelOnBoard[];
  eetMins?: number; //undefined = the leg's default (share of the flight EET)
};

export type WbSheetLeg = {
  info?: FlightLegInfo; //undefined for a flight without stops
  input: WbLoadsheetInput;
  calc: CalculatedFlightWbPoints;
  tripFuelMass: number;
  errors: string[];
  warnings: string[];
  fuelCarried: boolean;
  eetMins: number;
};

export type UseWbSheetOptions = {
  flight: Flight;
  initialWb?: FlightWb;
  isLatArmInWbPointUp: boolean;
  //passengers who didn't show up — their loads are dropped from every leg
  noShowIds?: string[];
  //a last-minute change: leg 1 fuel is the pilot's actual fuel, so the W&B is always saved per leg
  //(even for a flight without stops) to keep it
  revision?: boolean;
};

const isNoShow = (load: FlightWbLoad, flight: Flight, noShowIds: string[]) => {
  if (load.personKind === "passenger" && load.personId) return noShowIds.includes(load.personId);
  //loads saved before person ids: fall back to the passenger's last name
  if (load.personId === undefined && load.loadType !== "other" && load.station.type === "seat") {
    return flight.passengers.some((p) => p.id !== undefined && noShowIds.includes(p.id) && p.lastName === load.name);
  }
  return false;
};

function initialLegStates(flight: Flight, legs: FlightLegInfo[], initialWb: FlightWb | undefined): { states: LegState[]; fromSavedLegs?: boolean; seedError?: string } {
  const legCount = Math.max(1, legs.length);
  const savedLegs = initialWb?.legs;
  if (savedLegs && savedLegs.length === legCount) {
    //saved legs keep their fuel, leg 1 included: it is either the planned fuel of the flight form
    //(changing that clears the W&B) or the pilot's actual fuel from a last-minute change. A leg without
    //an uplift stays carried over, so it keeps following the fuel of the legs before it.
    return {
      fromSavedLegs: true,
      states: savedLegs.map((l, i) => ({ loads: l.loads, fuelOnBoard: i > 0 && l.fuelCarried ? undefined : l.fuelOnBoard, eetMins: l.eetMins })),
    };
  }
  try {
    const first = initialWb?.loads !== undefined && initialWb.loads.length > 0 ? initialWb.loads : generateSeatWbLoads(flight, legs.length > 1 ? 0 : undefined);
    const states: LegState[] = [{ loads: first }];
    for (var i = 1; i < legCount; i++) {
      states.push({ loads: generateLegWbLoads(flight, i, states[i - 1].loads) });
    }
    return { states };
  } catch (e) {
    return { states: Array.from({ length: legCount }, () => ({ loads: [] })), seedError: (e as Error).message };
  }
}

const NO_SHOWS: string[] = [] //stable default, so the per-leg memo doesn't recompute every render

export function useWbSheet({ flight, initialWb, isLatArmInWbPointUp, noShowIds = NO_SHOWS, revision = false }: UseWbSheetOptions) {
  const aircraft = flight.aircraft;
  const legs = useMemo(() => {
    const all = getFlightLegs(flight);
    return all.length > 1 ? all : [];
  }, [flight]);

  const initial = useMemo(() => initialLegStates(flight, legs, initialWb), [flight, legs, initialWb]);
  const [legStates, setLegStates] = useState<LegState[]>(initial.states);
  const [activeLeg, setActiveLeg] = useState(0);
  const seedError = initial.seedError;

  //legs are computed in order: a carried-over leg departs with the previous leg's landing fuel
  const sheetLegs: WbSheetLeg[] = useMemo(() => {
    const out: WbSheetLeg[] = [];
    legStates.forEach((state, i) => {
      const info = legs[i];
      const loads = state.loads.filter((l) => !isNoShow(l, flight, noShowIds));
      const fuelCarried = i > 0 && state.fuelOnBoard === undefined;
      const fuelOnBoard: FlightFuelOnBoard[] =
        i === 0 ? state.fuelOnBoard ?? flight.fuelOnBoard ?? []
        : state.fuelOnBoard ?? out[i - 1].calc.landing.landingFuelOnBoard;
      const eetMins = state.eetMins ?? info?.defaultEetMins ?? flight.totalEETMins;
      const tripFuelMass = Math.round(FuelConverter.toMassUnit(aircraft, aircraft.fuelPerHour * (eetMins / 60)));
      const calc = getCalculatedFlightWbPoints(aircraft, loads, fuelOnBoard, tripFuelMass);

      const texts = calculateOutOfLimitsTexts(calc, aircraft);
      //per-station structural limits (own maxMass + combined seat/bay placard limits) — entering
      //over-limit is allowed in the UI (never silently clamped) but blocks an "ok" save and shows
      //in the status band
      const hardTexts = [...texts, ...calculateStationLimitTexts(aircraft, loads, fuelOnBoard)];
      const warningTexts = calculateWarningOutOfLimitsTexts(calc, aircraft);
      //when hard errors exist, fold warnings into the error list (same behavior as before)
      const errors = hardTexts.length > 0 ? [...hardTexts, ...warningTexts] : hardTexts;
      const warnings = hardTexts.length > 0 ? [] : warningTexts;

      out.push({
        info,
        calc,
        tripFuelMass,
        errors,
        warnings,
        fuelCarried,
        eetMins,
        input: {
          flight,
          wbLoads: loads,
          fuelOnBoard,
          calc,
          tripFuelMass,
          errors,
          warnings,
          isLatArmInWbPointUp,
          leg: info ? { index: i, count: legs.length, from: info.from.name, to: info.to.name, eetMins } : undefined,
        },
      });
    });
    return out;
  }, [legStates, legs, flight, aircraft, noShowIds, isLatArmInWbPointUp]);

  const current = sheetLegs[Math.min(activeLeg, sheetLegs.length - 1)];

  // ── editing (always on the active leg) ──
  const updateLoads = useCallback(
    (fn: (prev: FlightWbLoad[]) => FlightWbLoad[]) =>
      setLegStates((prev) => prev.map((s, i) => (i === activeLeg ? { ...s, loads: fn(s.loads) } : s))),
    [activeLeg]
  );

  const setSeatMass = (station: AircraftWbStation, mass: number) => {
    const v = Math.max(0, Math.round(mass));
    updateLoads((prev) => {
      const existing = prev.find((l) => l.station.id === station.id);
      if (existing) {
        //clearing a cargo load removes it entirely (persons keep their seat at 0)
        if (existing.loadType === "other" && v === 0) {
          return prev.filter((l) => l.station.id !== station.id);
        }
        return prev.map((l) => (l.station.id === station.id ? { ...l, mass: v } : l));
      }
      if (v === 0) return prev;
      //weight entered on an EMPTY seat = no person attached → mark it as cargo, so the
      //Cabin view shows the same thing (a strapped box, excluded from pax counting)
      return [...prev, { station, mass: v, name: "Cargo", loadType: "other" as const }];
    });
  };

  const setBagMass = (station: AircraftWbStation, mass: number) => {
    //no clamp to maxMass: over-limit stays visible (red max hint + error) instead of being silently truncated
    const v = Math.max(0, Math.round(mass));
    updateLoads((prev) => {
      const existing = prev.find((l) => l.station.id === station.id);
      if (existing) {
        return prev.map((l) => (l.station.id === station.id ? { ...l, mass: v } : l));
      }
      return [...prev, { station, mass: v, name: station.name }];
    });
  };

  //swap whatever sits on two seat stations (either side may be empty → plain move).
  //Used by both the list's ▲▼ carets and the cabin map's tap-tap interaction.
  const swapSeats = (stationIdA: string, stationIdB: string) => {
    const stations = aircraft.wb!.stations;
    const a = stations.find((s) => s.id === stationIdA);
    const b = stations.find((s) => s.id === stationIdB);
    if (!a || !b || a.id === b.id) return;
    updateLoads((prev) => prev.map((l) => (l.station.id === a.id ? { ...l, station: b } : l.station.id === b.id ? { ...l, station: a } : l)));
  };

  const moveSeat = (stationId: string, dir: -1 | 1) => {
    const seatStations = aircraft.wb!.stations.filter((s) => s.type === "seat");
    const idx = seatStations.findIndex((s) => s.id === stationId);
    const targetIdx = idx + dir;
    if (idx === -1 || targetIdx < 0 || targetIdx >= seatStations.length) return;
    swapSeats(stationId, seatStations[targetIdx].id);
  };

  //cargo strapped on a seat (ForeFlight's “Other” load) — replaces whatever load sits there; 0 removes it
  const placeCargoOnSeat = (station: AircraftWbStation, mass: number) => {
    const v = Math.max(0, Math.round(mass));
    updateLoads((prev) => {
      const rest = prev.filter((l) => l.station.id !== station.id);
      return v === 0 ? rest : [...rest, { station, mass: v, name: "Cargo", loadType: "other" as const }];
    });
  };

  const removeSeatLoad = (stationId: string) => {
    updateLoads((prev) => prev.filter((l) => l.station.id !== stationId));
  };

  //departure fuel of a leg, per tank, in the aircraft's fuel unit. Editing a carried-over leg starts
  //from the fuel it currently shows (the previous leg's landing fuel).
  const setLegFuelAt = (legIndex: number, stationId: string, fuel: number) => {
    const legFuel = sheetLegs[legIndex].input.fuelOnBoard;
    const tanks = aircraft.wb!.stations.filter((s) => s.type === "fuel");
    const next: FlightFuelOnBoard[] = tanks.map((station) => {
      const existing = legFuel.find((f) => f.station.id === station.id);
      if (station.id !== stationId) return existing ?? { station, fuel: 0, mass: 0 };
      const v = Math.max(0, fuel);
      return { station, fuel: v, mass: Math.round(FuelConverter.toMassUnit(aircraft, v)) };
    });
    setLegStates((prev) => prev.map((s, i) => (i === legIndex ? { ...s, fuelOnBoard: next } : s)));
  };
  const setLegFuel = (stationId: string, fuel: number) => setLegFuelAt(activeLeg, stationId, fuel);

  //back to carrying the previous leg's landing fuel (legs 2+) / the planned fuel (leg 1)
  const resetLegFuelAt = (legIndex: number) => setLegStates((prev) => prev.map((s, i) => (i === legIndex ? { ...s, fuelOnBoard: undefined } : s)));
  const resetLegFuel = () => resetLegFuelAt(activeLeg);

  const setLegEet = (mins: number) =>
    setLegStates((prev) => prev.map((s, i) => (i === activeLeg ? { ...s, eetMins: Math.max(0, Math.round(mins)) } : s)));

  const hasErrors = sheetLegs.some((l) => l.errors.length > 0);

  //what gets saved on the flight: the same shape for a one-leg flight as before this change
  const buildWb = (): FlightWb => {
    const errorTexts = sheetLegs.flatMap((l, i) => (legs.length > 1 ? l.errors.map((t) => `Leg ${i + 1}: ${t}`) : l.errors));
    const status = hasErrors ? "error" : "ok";
    const base: FlightWb = { status, loads: sheetLegs[0].input.wbLoads, ...(hasErrors ? { errors: errorTexts } : {}) };
    //a one-leg flight saves legs only when it carries the pilot's actual fuel (last-minute change)
    if (legs.length <= 1 && !revision && !initial.fromSavedLegs) return base;
    return {
      ...base,
      legs: sheetLegs.map((l, i) => ({
        fromIndex: legs[i]?.fromIndex ?? 0,
        toIndex: legs[i]?.toIndex ?? 1,
        loads: l.input.wbLoads,
        fuelOnBoard: l.input.fuelOnBoard,
        ...(l.fuelCarried ? { fuelCarried: true } : {}),
        eetMins: l.eetMins,
        status: l.errors.length > 0 ? "error" : "ok",
        ...(l.errors.length > 0 ? { errors: l.errors } : {}),
      })),
    };
  };

  return {
    legs,
    sheetLegs,
    current,
    activeLeg,
    setActiveLeg,
    seedError,
    hasErrors,
    setSeatMass,
    setBagMass,
    swapSeats,
    moveSeat,
    placeCargoOnSeat,
    removeSeatLoad,
    setLegFuel,
    setLegFuelAt,
    resetLegFuel,
    resetLegFuelAt,
    setLegEet,
    buildWb,
  };
}


